/**
 * DocumentFormPage — Página completa para crear/editar documentos de venta.
 *
 * Reemplaza el modal estrecho con un layout de scroll continuo + sidebar sticky.
 * Secciones se muestran/ocultan según el tipo de DTE seleccionado y los campos
 * habilitados en DTETypeConfig del tenant.
 *
 * Ruta: /sales/documents/new  o  /sales/documents/:id/edit
 */
import { todayISO } from '../../../lib/utils'

/** Desde esta fecha rige la Res. Ex. SII 144 (transporte obligatorio en la guía). */
const VIGENCIA_RES_144 = '2026-11-01'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import DatePicker from '@/components/DatePicker'
import { NumericInput } from '@/components/NumericInput'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { useTenantLocale, formatMoneyForTenant, useTenantCurrency, currencyDecimals, useForeignCurrencies, useEmiteEnMonedaExtranjera } from '@/lib/locale'
import { totalesDelDocumento, subtotalDeLinea, subtotalDeLineaEnSuMoneda, precioEnMonedaBase } from '../lib/documentTotals'
import { inputCls } from '@/lib/formStyles'
import { paisUsaDetraccion, CODIGOS_TIPO_OPERACION_PE } from '@/lib/documentNumbering'
import {
  ArrowLeft, FileText, UserCircle, Truck, Globe2, List, Tag,
  Link2, Banknote, StickyNote, Send, Save, Plus, Trash2, Loader2,
  Check, X, ClipboardList, MapPin, TreePine, Building2,
  Paperclip, Upload,
} from 'lucide-react'
import { CostCenterAllocator } from '@/modules/accounting/components/CostCenterAllocator'
import { FolioStatusNotice } from '@/modules/config/components/FolioStatusNotice'
import type { CostCenterAllocation } from '@/modules/accounting/components/CostCenterAllocator'
import { Autocomplete } from '@/components/Autocomplete'
import type { AutocompleteItem } from '@/components/Autocomplete'
import { formatRut } from '@/lib/utils'
import {
  useCustomers, useSaleDocument,
  useCreateDocument, useUpdateDocument, useEmitDocument,
  useDocumentAttachments, useUploadDocumentAttachment, useDeleteDocumentAttachment,
  uploadDocumentAttachmentFile,
} from '@/modules/sales/hooks/useSales'
import { ProductAutocomplete } from './ProductAutocomplete'
import { useProductLookup } from '../hooks/useProductLookup'
import { PendingAttachmentsPicker } from '@/modules/sales/components/PendingAttachmentsPicker'
import type {
  SaleDocument, SaleDocumentLine, CustomerListItem, ProductListItem,
  DocumentReferencia, DocumentPersonalizado,
} from '@/modules/sales/hooks/useSales'
import {
  useLocalizationConfig, useGlobalCustomFields, useCurrencyRates, useDTETypes, useDTEFieldSchema,
  useTenantTax,
} from '@/modules/config/hooks/useConfig'
import { useAllStock } from '@/modules/inventory/hooks/useInventory'
import { AlertTriangle, HelpCircle, Eye } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import type { CurrencyRateInfo, GlobalCustomField } from '@/modules/config/hooks/useConfig'
import { HelpPanel } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { CustomerFinancialCard } from '@/modules/sales/components/three60/CustomerFinancialCard'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Tooltip } from '@/components/ds/Tooltip'

// ─── Constants ───────────────────────────────────────────────────────────────

const readonlyCls = 'w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface-muted text-content-muted cursor-default'
const labelCls = 'block text-xs font-medium text-content-muted mb-1'
const cellInputCls = 'w-full px-2 py-1.5 text-xs border border-divider rounded bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 transition-all'

const DOCUMENT_TYPES_FALLBACK = [
  { value: 'FE', label: 'Factura Electrónica' },
  { value: 'BE', label: 'Boleta Electrónica' },
  { value: 'NC', label: 'Nota de Crédito' },
  { value: 'ND', label: 'Nota de Débito' },
  { value: 'FC', label: 'Factura de Compra' },
]

const UNITS = ['UN', 'KG', 'MT', 'LT', 'HR', 'DY', 'MO', 'BX', 'PK', 'OT']

/**
 * Una fila de las tablas de este formulario: su identidad y su POSICIÓN.
 *
 * 🔴 El índice viaja DENTRO del dato porque los campos se nombran por posición
 * (`referencias.2.folio_ref`) y el `render` de la tabla recibe el índice de la
 * fila YA ORDENADA: con el del render, el día que alguien marque una columna
 * `sortable` cada campo escribiría en otra fila, sin ningún error.
 */
type FilaDeFormulario = { id: string; idx: number }

/** Primer mensaje de error (string) dentro de un árbol de detalles DRF, incluso
 *  anidado como `{lines: [{unit_price: ['...']}]}`. Para surfacear al usuario el
 *  error real (p. ej. "El precio unitario debe ser mayor que 0"), no un genérico. */
function firstErrorString(v: unknown): string | null {
  if (typeof v === 'string') return v
  if (Array.isArray(v)) {
    for (const x of v) { const s = firstErrorString(x); if (s) return s }
    return null
  }
  if (v && typeof v === 'object') {
    for (const x of Object.values(v)) { const s = firstErrorString(x); if (s) return s }
    return null
  }
  return null
}

/** Tipos de documento de referencia comunes del SII */
const REF_DOC_TYPES = [
  { group: 'DTEs', options: [
    { value: '33', label: '33 — Factura Electrónica' },
    { value: '34', label: '34 — Factura No Afecta o Exenta' },
    { value: '39', label: '39 — Boleta Electrónica' },
    { value: '41', label: '41 — Boleta No Afecta o Exenta' },
    { value: '43', label: '43 — Liquidación-Factura' },
    { value: '46', label: '46 — Factura de Compra' },
    { value: '48', label: '48 — Comprobante de Pago Electrónico' },
    { value: '52', label: '52 — Guía de Despacho' },
    { value: '56', label: '56 — Nota de Débito' },
    { value: '61', label: '61 — Nota de Crédito' },
    { value: '110', label: '110 — Factura de Exportación' },
    { value: '111', label: '111 — N.D. de Exportación' },
    { value: '112', label: '112 — N.C. de Exportación' },
  ]},
  { group: 'Documentos Comerciales', options: [
    { value: '801', label: '801 — Orden de Compra' },
    { value: '802', label: '802 — Nota de Pedido' },
    { value: '803', label: '803 — Contrato' },
    { value: '804', label: '804 — Resolución' },
    { value: '805', label: '805 — Proceso ChileCompra' },
    { value: '806', label: '806 — Ficha ChileCompra' },
    { value: 'HES', label: 'HES — H. Entrada de Servicios' },
    { value: 'HAS', label: 'HAS — H. Aceptación de Servicios' },
  ]},
]

const EMPTY_LINE: SaleDocumentLine = {
  sku: '', name: '', description: '', unit: 'UN',
  quantity: '1', unit_price: '0', price_currency: '',
  discount_pct: '0', is_tax_exempt: false, tax_data: {},
}

const EMPTY_REF: DocumentReferencia = {
  tipo_doc: '', folio_ref: '', fecha_ref: '', cod_ref: '', razon_ref: '',
}

interface DscRcgLine {
  tpo_mov: 'D' | 'R'
  glosa_dr: string
  tpo_valor: '%' | '$'
  valor_dr: string
}

const EMPTY_DSC_RCG: DscRcgLine = {
  tpo_mov: 'D', glosa_dr: '', tpo_valor: '%', valor_dr: '',
}

interface GlobalCampoValue {
  id: number
  nombre: string
  tipo: 'string' | 'numero' | 'fecha' | 'select' | 'multiselect' | 'boolean' | 'entity_link'
  is_required: boolean
  valor: string
  opciones?: string[]
}

interface DocumentFormData {
  customer: string
  document_type: string
  issue_date: string
  currency?: string
  payment_method: 'contado' | 'credito'
  /** Catálogo 51 de SUNAT (solo Perú). Vacío = venta interna. */
  operation_type?: string
  payment_term_days: number
  notes: string
  lines: SaleDocumentLine[]
  referencias: DocumentReferencia[]
  dsc_rcg_global: DscRcgLine[]
  global_campos: GlobalCampoValue[]
  personalizados: DocumentPersonalizado[]
  // Localization extra fields
  ind_traslado?: string
  transporte?: Record<string, string>
  aduana?: Record<string, string>
}

// NC/ND/NDX/NCX show "Obligatorio" badge on Referencias
const REFERENCE_TYPES = new Set(['NC', 'ND', 'NDX', 'NCX'])

// Section definitions for nav — fieldKey links to dte_schema.py catalog key
interface SectionDef {
  id: string
  label: string
  icon: typeof FileText
  color: string
  fieldKey?: string  // DTE catalog key — section hidden if field not enabled
}

const SECTIONS: SectionDef[] = [
  { id: 'sec-documento', label: 'Documento', icon: FileText, color: 'bg-primary-100 text-primary-hover dark:text-primary-200' },
  { id: 'sec-receptor', label: 'Receptor', icon: UserCircle, color: 'bg-success-surface-strong text-success-content' },
  { id: 'sec-transporte', label: 'Transporte', icon: Truck, color: 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-200', fieldKey: 'transporte' },
  { id: 'sec-aduana', label: 'Aduana / Exportación', icon: Globe2, color: 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-200', fieldKey: 'aduana' },
  { id: 'sec-detalle', label: 'Líneas de Detalle', icon: List, color: 'bg-info-surface-strong text-info-content' },
  { id: 'sec-descuentos', label: 'Descuentos / Recargos', icon: Tag, color: 'bg-warning-surface-strong text-warning-content', fieldKey: 'dsc_rcg_global' },
  { id: 'sec-referencias', label: 'Referencias', icon: Link2, color: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-200' },
  { id: 'sec-comisiones', label: 'Comisiones', icon: Banknote, color: 'bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-200', fieldKey: 'comisiones' },
  { id: 'sec-geo', label: 'Georreferencia', icon: MapPin, color: 'bg-lime-100 dark:bg-lime-900/50 text-lime-700 dark:text-lime-200', fieldKey: 'geo_ref_emision' },
  { id: 'sec-madera', label: 'Manejo de Madera', icon: TreePine, color: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-200', fieldKey: 'manejo_madera' },
  { id: 'sec-campos', label: 'Campos Personalizados', icon: ClipboardList, color: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-200' },
  { id: 'sec-adjuntos', label: 'Adjuntos', icon: Paperclip, color: 'bg-warning-surface-strong text-warning-content' },
  { id: 'sec-notas', label: 'Notas', icon: StickyNote, color: 'bg-surface-muted text-content' },
]

// ─── Format helpers ──────────────────────────────────────────────────────────

const fmtCLP = (v: number) =>
  formatMoneyForTenant(v)

// ─── Component ───────────────────────────────────────────────────────────────

export interface DocumentFormTotals {
  neto: number; exento: number; iva: number; total: number
  foreignCurrency: string | null; rate: number | null
}

export function DocumentFormPage({ embedded = false, onTotalsChange }: {
  embedded?: boolean
  /** En modo embebido (Vista 360) reporta los totales en vivo para el header. */
  onTotalsChange?: (t: DocumentFormTotals) => void
} = {}) {
  const navigate = useNavigate()
  const { id: editId } = useParams<{ id: string }>()
  const isEditing = !!editId

  // ── Data queries ──
  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const { data: stockData } = useAllStock({ page_size: 9999 })
  const { data: locConfig } = useLocalizationConfig()
  const { data: globalFields = [] } = useGlobalCustomFields(true)
  const { data: currencyRatesData } = useCurrencyRates()
  const { data: dteTypesData } = useDTETypes()
  const { data: editDoc } = useSaleDocument(editId ?? null)

  const tax = useTenantTax()
  const taxRate = tax.rate
  // El tipo de operación es peruano (catálogo 51). En Chile no existe el
  // concepto, así que el selector solo aparece donde significa algo.
  const usaTipoOperacion = paisUsaDetraccion(locConfig?.country_code)
  const customers = customersData?.customers || []
  const currencyRates = currencyRatesData?.rates as Record<string, CurrencyRateInfo> | undefined

  // Stock disponible por producto (suma todas las bodegas)
  const stockMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (const s of (stockData?.stock || [])) {
      const pid = s.product_id
      map[pid] = (map[pid] || 0) + parseFloat(s.available_quantity)
    }
    return map
  }, [stockData])

  const DOCUMENT_TYPES = dteTypesData
    ? dteTypesData.filter(t => t.is_active).map(t => ({ value: t.internal_code, label: t.display_name }))
    : DOCUMENT_TYPES_FALLBACK

  // ── Mutations ──
  const createMutation = useCreateDocument()
  const updateMutation = useUpdateDocument(editId || '')
  const emitMutation = useEmitDocument()
  // Adjuntos buffereados al CREAR (sin id padre todavía) — se suben tras crear.
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [savingAttachments, setSavingAttachments] = useState(false)
  const isPending = createMutation.isPending || updateMutation.isPending || savingAttachments

  // ── Attachments ──
  const { data: attachmentsData } = useDocumentAttachments(editId ?? null)
  const uploadAttachment = useUploadDocumentAttachment(editId ?? '')
  const deleteAttachment = useDeleteDocumentAttachment(editId ?? '')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError(null)
    try {
      await uploadAttachment.mutateAsync(file)
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || 'Error al subir el archivo.'
      setUploadError(msg)
    }
    e.target.value = ''
  }

  // ── Form ──
  const today = todayISO()
  const locale = useTenantLocale()
  const baseCurrency = useTenantCurrency()
  const emiteEnMonedaExtranjera = useEmiteEnMonedaExtranjera()
  const monedasExtranjeras = useForeignCurrencies()

  // Permite preseleccionar el tipo vía ?type= (p. ej. "Nueva Guía" desde /sales/guides
  // abre el formulario con Guía de Despacho ya elegida). Solo se aplica al crear.
  const [searchParams] = useSearchParams()
  const initialDocType = searchParams.get('type') === 'GD' ? 'GD' : 'FE'
  const toast = useToast()

  const {
    register, handleSubmit, control, watch, setValue, reset,
    formState: { errors },
  } = useForm<DocumentFormData>({
    defaultValues: {
      customer: '', document_type: initialDocType, issue_date: today, currency: '',
      payment_method: 'contado', payment_term_days: 0, notes: '',
      lines: [{ ...EMPTY_LINE }], referencias: [], dsc_rcg_global: [],
      global_campos: [], personalizados: [],
    },
  })

  const { fields: lineFields, append: appendLine, remove: removeLine } = useFieldArray({ control, name: 'lines' })
  const { fields: refFields, append: appendRef, remove: removeRef } = useFieldArray({ control, name: 'referencias' })
  const { fields: dscRcgFields, append: appendDscRcg, remove: removeDscRcg } = useFieldArray({ control, name: 'dsc_rcg_global' })
  const { fields: globalCampoFields, remove: removeGlobalCampo } = useFieldArray({ control, name: 'global_campos' })

  // Build global_campos from active global fields + existing personalizados
  const buildGlobalCampos = (existingPersonalizados: DocumentPersonalizado[] = []) => {
    const activeGlobals = (globalFields as GlobalCustomField[])
      .filter(f => f.is_active)
      .sort((a, b) => a.order - b.order)
    const globalNombres = new Set(activeGlobals.map(f => f.nombre))
    const campos: GlobalCampoValue[] = activeGlobals.map(f => {
      const existing = existingPersonalizados.find(p => p.nombre === f.nombre)
      return {
        id: f.id, nombre: f.nombre, tipo: f.tipo,
        is_required: f.is_required, valor: existing?.valor ?? f.default_value,
        opciones: f.opciones || [],
      }
    })
    const adHoc = existingPersonalizados.filter(p => !globalNombres.has(p.nombre))
    return { campos, adHoc }
  }

  // Load edit document — SOLO una vez por documento. Sin el guard, un refetch de
  // `editDoc` o `globalFields` (TanStack Query, window-focus/staleTime) re-ejecuta
  // reset() y bota lo que el usuario está editando (mismo bug de NC del modal:
  // razón/cod_ref de la referencia volvían al valor original). Ver DocumentModal.
  const loadedEditKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isEditing) { loadedEditKeyRef.current = null; return }
    if (editDoc && loadedEditKeyRef.current !== editDoc.id) {
      loadedEditKeyRef.current = editDoc.id
      const editLines = editDoc.lines?.length > 0
        ? editDoc.lines.map(l => ({
            ...l,
            unit_price: l.unit_price_original && l.price_currency && l.price_currency !== baseCurrency
              ? l.unit_price_original : l.unit_price,
          }))
        : [{ ...EMPTY_LINE }]
      const { campos, adHoc } = buildGlobalCampos(editDoc.personalizados)
      reset({
        customer: editDoc.customer || '',
        document_type: editDoc.document_type || 'FE',
        issue_date: editDoc.issue_date || today,
        currency: (editDoc as any)?.currency || '',
        payment_method: (editDoc as any)?.payment_method || 'contado',
        payment_term_days: (editDoc as any)?.payment_term_days || 0,
        notes: editDoc.notes || '',
        lines: editLines,
        referencias: editDoc.referencias || [],
        dsc_rcg_global: [],
        global_campos: campos,
        personalizados: adHoc,
        ind_traslado: editDoc.guia?.ind_traslado || '',
        transporte: editDoc.guia?.transporte || {},
      })
    }
  }, [editDoc, isEditing, reset, today, globalFields])

  // Init global_campos for new documents when globalFields load
  const globalFieldsInitRef = useRef(false)
  useEffect(() => {
    if (!isEditing && globalFields.length > 0 && !globalFieldsInitRef.current) {
      globalFieldsInitRef.current = true
      const { campos } = buildGlobalCampos()
      if (campos.length > 0) setValue('global_campos', campos)
    }
  }, [globalFields, isEditing, setValue])

  // Preseleccionar el tipo desde ?type= una vez que cargan los tipos DTE del tenant.
  // El <select> de tipo es no-controlado: si al montar el tipo pedido aún no está
  // entre las opciones (dteTypesData es async y el fallback no incluye GD), el DOM no
  // puede aplicarlo y queda en FE aunque el valor RHF sea el correcto. Re-aplicamos
  // con setValue cuando llegan los tipos, y SOLO si el tipo está activo en
  // /config/dte-types; si no, volvemos a FE (no forzar un tipo deshabilitado).
  const docTypeInitRef = useRef(false)
  useEffect(() => {
    if (isEditing || docTypeInitRef.current || !dteTypesData) return
    docTypeInitRef.current = true
    const t = searchParams.get('type')
    if (!t) return
    const active = dteTypesData.some(dt => dt.is_active && dt.internal_code === t)
    setValue('document_type', active ? t : 'FE')
  }, [dteTypesData, isEditing, searchParams, setValue])

  // ── Watched values ──
  const watchedDocType = watch('document_type')

  // ── Enabled fields from DTE config (mandatory + tenant-enabled) ──
  const currentDteConfig = dteTypesData?.find(t => t.internal_code === watchedDocType)
  const { data: fieldSchema } = useDTEFieldSchema(currentDteConfig?.sii_doc_type)
  const enabledFieldKeys = useMemo(() => {
    if (!fieldSchema || !currentDteConfig) return null // null = show all (legacy)
    const mandatoryKeys = new Set<string>()
    const enabledKeys = new Set(currentDteConfig.enabled_fields)
    for (const fields of Object.values(fieldSchema.fields_by_zone)) {
      for (const f of fields) {
        if (f.obligation === 1) mandatoryKeys.add(f.key)
      }
    }
    return new Set([...mandatoryKeys, ...enabledKeys])
  }, [fieldSchema, currentDteConfig])

  /** Check if a DTE field key is enabled (mandatory or tenant-configured). null = legacy/show all */
  const isFieldEnabled = (key: string) => enabledFieldKeys === null || enabledFieldKeys.has(key)

  const watchedCustomerId = watch('customer')
  const watchedLines = watch('lines')
  // Los productos de las líneas ya cargadas más los elegidos en la sesión. La
  // búsqueda va al servidor (ProductAutocomplete): bajar los primeros 500 dejaba
  // sin poder buscar al resto del catálogo.
  const { products, remember: recordarProducto } = useProductLookup(watchedLines as any)
  const watchedPaymentMethod = watch('payment_method')
  const watchedPaymentTermDays = watch('payment_term_days')
  const watchedIssueDate = watch('issue_date')
  // Res. Ex. SII 144/2026: desde el 1-nov-2026 la guía identifica chofer, patente,
  // transportista, destino y fecha de salida. Cuenta el día en que sale al SII, así
  // que también un borrador fechado antes. La misma regla está en validators_emission.
  const exigeTransporte = watchedDocType === 'GD'
    && [watchedIssueDate || '', today].some(fecha => fecha >= VIGENCIA_RES_144)
  const transporteRequerido = (falta: string) => ({
    validate: (v?: string) => !exigeTransporte || !!(v || '').trim() || `Falta ${falta}.`,
  })
  const errorDeTransporte = (campo: string) => {
    const mensaje = (errors.transporte as Record<string, { message?: string }> | undefined)?.[campo]?.message
    return mensaje ? <p className="mt-1 text-xs text-danger-solid">{mensaje}</p> : null
  }
  const marcaObligatoria = watchedDocType === 'GD' ? <span className="text-danger-solid">*</span> : null

  // ── Receptor auto-fill ──
  const selectedCustomer = customers.find(c => c.id === watchedCustomerId)
  useEffect(() => {
    if (selectedCustomer && !isEditing) {
      const cust = selectedCustomer as any
      if (cust.payment_term_days && cust.payment_term_days > 0) {
        setValue('payment_method', 'credito')
        setValue('payment_term_days', cust.payment_term_days)
      }
    }
  }, [watchedCustomerId, selectedCustomer, isEditing, setValue])

  // ── Totals calculation ──
  const getRate = (currency: string): number | null => {
    if (!currency || currency === baseCurrency || !currencyRates) return null
    const info = currencyRates[currency]
    return info ? parseFloat(info.rate) : null
  }

  // Los totales salen de `documentTotals`, que replica el orden de operaciones
  // del backend (redondear el precio convertido, después el subtotal de cada
  // línea, y recién ahí sumar). Escribirlo acá a mano es como el preview y lo
  // guardado terminaron dando números distintos.
  const opcionesDeTotales = { baseCurrency, taxRate, getRate }
  const { montoNeto, montoExento, montoIVA, total, lineCount } =
    totalesDelDocumento(watchedLines as any, opcionesDeTotales)
  const foreignCurrency = watchedLines
    .map(l => (l as any).price_currency)
    .find(c => c && c !== baseCurrency) || null

  // Reportar totales en vivo al contenedor (Vista 360 → header como resumen).
  const fxRate = foreignCurrency && currencyRates?.[foreignCurrency]
    ? parseFloat(currencyRates[foreignCurrency].rate) : null
  useEffect(() => {
    onTotalsChange?.({ neto: montoNeto, exento: montoExento, iva: montoIVA, total, foreignCurrency, rate: fxRate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montoNeto, montoExento, montoIVA, total, foreignCurrency, fxRate])

  // ── Product auto-fill ──
  const fillProduct = (index: number, product: ProductListItem) => {
    setValue(`lines.${index}.product_id` as any, product.id)
    setValue(`lines.${index}.sku`, product.sku)
    setValue(`lines.${index}.name`, product.name)
    setValue(`lines.${index}.unit`, product.unit)
    setValue(`lines.${index}.is_tax_exempt`, product.is_tax_exempt)

    // Si el comprobante se emite en una moneda y el producto tiene precio en
    // ella, se usa ese. Sin esto el usuario elegia «emitir en USD» y cada linea
    // entraba en la moneda del producto, asi que el backend rechazaba el
    // documento entero por mezclar monedas — teniendo el precio en dolares
    // cargado y a mano.
    const monedaDoc = watch('currency')
    const enMonedaDelDoc = monedaDoc
      ? productPriceOptions(product).find(o => o.currency === monedaDoc)
      : undefined
    if (enMonedaDelDoc) {
      setValue(`lines.${index}.unit_price`, enMonedaDelDoc.unit_price)
      setValue(`lines.${index}.price_currency` as any, monedaDoc)
      return
    }
    setValue(`lines.${index}.unit_price`, product.unit_price)
    setValue(`lines.${index}.price_currency` as any, product.price_currency || baseCurrency)
  }

  // Precios disponibles de un producto: el por defecto + los adicionales por
  // moneda. Habilita el selector de moneda por línea (#multi-precio).
  const productPriceOptions = (product?: ProductListItem) => {
    if (!product) return [] as { currency: string; unit_price: string }[]
    const opts = [{ currency: product.price_currency || baseCurrency, unit_price: product.unit_price }]
    for (const p of product.prices ?? []) opts.push({ currency: p.currency, unit_price: p.unit_price })
    return opts
  }

  const findLineProduct = (line: any): ProductListItem | undefined =>
    (line?.product_id && products.find(p => p.id === line.product_id)) ||
    (line?.sku ? products.find(p => p.sku === line.sku) : undefined)

  // Cambiar la moneda de una línea desde el selector: aplica el precio de esa
  // moneda definido en el producto (preservando la moneda original de la venta).
  const changeLineCurrency = (index: number, product: ProductListItem, currency: string) => {
    const opt = productPriceOptions(product).find(o => o.currency === currency)
    setValue(`lines.${index}.price_currency` as any, currency)
    if (opt) setValue(`lines.${index}.unit_price`, opt.unit_price)
  }

  const customerItems: AutocompleteItem[] = customers.map(c => ({ id: c.id, label: c.legal_name, sublabel: c.tax_id ? formatRut(c.tax_id) : undefined }))

  // ── Expanded line rows (description + cost centers) ──
  const [helpOpen, setHelpOpen] = useState(false)
  /**
   * Cómo se imprime un tipo de cambio en los dos tooltips de la tabla de
   * líneas. Estaba escrito dos veces idéntico, incluida la regla de que la UF
   * lleva cuatro decimales y el resto dos.
   */
  const fmtTipoDeCambio = (rate: number, currency: string) =>
    new Intl.NumberFormat(locale, {
      style: 'currency', currency: baseCurrency,
      minimumFractionDigits: 2, maximumFractionDigits: currency === 'UF' ? 4 : 2,
    }).format(rate)

  const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set())

  const toggleLine = (idx: number) => {
    setExpandedLines(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  /** Las filas de la tabla de líneas — ver el comentario de la tabla. */
  const filasDeLinea = lineFields.map((field, idx) => {
    const linea = (watchedLines[idx] ?? {}) as any
    const cur = linea.price_currency || baseCurrency
    const lineCC = (watchedLines[idx]?.cost_center_allocations ?? []) as any[]
    const lineDesc = watchedLines[idx]?.description ?? ''
    return {
      id: field.id,
      idx,
      linea,
      cur,
      rate: getRate(cur),
      price: precioEnMonedaBase(linea, opcionesDeTotales),
      lineSub: subtotalDeLinea(linea, opcionesDeTotales),
      lineSubForeign: subtotalDeLineaEnSuMoneda(linea, opcionesDeTotales),
      lineCC,
      hasExtras: lineCC.length > 0 || lineDesc.length > 0,
    }
  })

  const handleRemoveLine = (idx: number) => {
    setExpandedLines(prev => {
      const next = new Set<number>()
      for (const i of prev) {
        if (i < idx) next.add(i)
        else if (i > idx) next.add(i - 1)
        // i === idx is dropped
      }
      return next
    })
    removeLine(idx)
  }

  // ── Visible sections (fieldKey controls visibility from DTE config) ──
  const visibleSections = SECTIONS.filter(s => {
    return !s.fieldKey || isFieldEnabled(s.fieldKey)
  })

  // ── Scroll spy (listens on the scrollable parent, not window) ──
  const [activeSection, setActiveSection] = useState('sec-documento')
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    // Find the nearest scrollable ancestor (AppLayout's <main overflow-y-auto>)
    const firstSection = document.getElementById('sec-documento')
    let container: HTMLElement | null = firstSection?.parentElement ?? null
    while (container && container !== document.documentElement) {
      const style = getComputedStyle(container)
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') break
      container = container.parentElement
    }
    // Fallback to window if no scrollable parent found
    const target = container || window
    scrollContainerRef.current = container

    const handleScroll = () => {
      for (const s of [...visibleSections].reverse()) {
        const el = document.getElementById(s.id)
        if (el) {
          const rect = el.getBoundingClientRect()
          if (rect.top <= 140) { setActiveSection(s.id); return }
        }
      }
    }
    target.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // run once on mount
    return () => target.removeEventListener('scroll', handleScroll)
  }, [visibleSections])

  // ── Submit ──
  const onSubmit = (data: DocumentFormData) => {
    // Merge global_campos into personalizados for backend
    const globalMerged = (data.global_campos || [])
      .filter(c => c.valor !== '' || c.is_required || c.tipo === 'boolean')
      .map(({ nombre, tipo, valor }) => ({ nombre, tipo, valor }))
    const allPersonalizados = [...globalMerged, ...(data.personalizados || [])]

    const payload: Partial<SaleDocument> = {
      customer: data.customer,
      document_type: data.document_type as any,
      issue_date: data.issue_date,
      payment_method: data.payment_method,
      payment_term_days: data.payment_term_days,
      notes: data.notes,
      lines: data.lines,
      referencias: data.referencias,
      personalizados: allPersonalizados,
      // Solo se manda donde aplica: en Chile el campo no existe y enviarlo vacío
      // no aporta nada al payload.
      ...(usaTipoOperacion ? { operation_type: data.operation_type || '' } : {}),
      // Idem con la moneda de emision: donde el pais no la admite, el backend
      // la rechaza, asi que ni se manda.
      ...(emiteEnMonedaExtranjera ? { currency: data.currency || '' } : {}),
      // Guía de despacho: el tipo de traslado y el transporte van al XML. Hasta el
      // #1577 el formulario los pedía pero no los mandaba, y la guía salía al SII
      // sin IndTraslado.
      ...(data.document_type === 'GD'
        ? { ind_traslado: data.ind_traslado || '', transporte: data.transporte || {} }
        : {}),
    }
    // Las Guías de Despacho viven en /sales/guides (se excluyen del listado de
    // documentos), así que al guardar una GD volvemos ahí y no al listado de ventas.
    const backTo = data.document_type === 'GD' ? '/sales/guides' : '/sales/documents'
    // Surface del error: antes ambas mutaciones no tenían onError, así que un 400
    // del backend (p. ej. "El precio unitario debe ser mayor que 0") se tragaba en
    // silencio y el usuario no sabía por qué no guardaba.
    const showError = (e: unknown) => {
      const details = (e as { response?: { data?: { error?: { details?: unknown } } } })
        ?.response?.data?.error?.details
      toast.error(firstErrorString(details) || extractErrorMessage(e, 'No se pudo guardar el documento.'))
    }
    if (isEditing) {
      updateMutation.mutate(payload, { onSuccess: () => navigate(backTo), onError: showError })
    } else {
      createMutation.mutate(payload, {
        onError: showError,
        onSuccess: async (created: { id?: string }) => {
          // Subir los adjuntos buffereados ahora que el documento tiene id.
          if (created?.id && pendingFiles.length > 0) {
            setSavingAttachments(true)
            const failed: string[] = []
            for (const file of pendingFiles) {
              try {
                await uploadDocumentAttachmentFile(created.id, file)
              } catch {
                failed.push(file.name)
              }
            }
            setSavingAttachments(false)
            // Si alguna subida falló, llevar a la edición del documento creado
            // para que el usuario vea lo que sí se adjuntó y re-suba lo demás.
            if (failed.length > 0) {
              navigate(`/sales/documents/${created.id}/edit`)
              return
            }
          }
          navigate(backTo)
        },
      })
    }
  }

  // ── Due date calculation ──
  const dueDate = useMemo(() => {
    if (watchedPaymentMethod !== 'credito' || !watchedPaymentTermDays || !watchedIssueDate) return null
    const d = new Date(watchedIssueDate + 'T12:00:00')
    d.setDate(d.getDate() + watchedPaymentTermDays)
    return d.toLocaleDateString('es-CL')
  }, [watchedPaymentMethod, watchedPaymentTermDays, watchedIssueDate])

  // ─── RENDER ────────────────────────────────────────────────────────────────

  return (
    <div data-tour="docform-page" className={embedded ? '' : 'min-h-screen bg-surface-muted'}>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
      {/* Header — oculto en modo embebido (el contenedor 360 aporta su propio header) */}
      {!embedded && (
      <header className="sticky top-0 z-40 bg-surface border-b border-divider shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/sales/documents')} className="flex items-center gap-1.5 text-xs text-content-muted hover:text-primary transition-colors group">
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              Volver
            </button>
            <div className="w-px h-5 bg-surface-muted" />
            <span className="text-xs font-medium text-content">
              {isEditing ? 'Editar documento' : 'Nuevo documento'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-warning-surface text-warning-content border border-warning-border">
              Borrador
            </span>
            <button
              type="button"
              data-tour="docform-help"
              onClick={() => setHelpOpen(true)}
              className="relative p-1.5 rounded-lg text-content-subtle hover:text-primary hover:bg-primary-50 transition-colors"
              title="Ayuda sobre este formulario"
            >
              <HelpCircle size={16} />
              <span className="absolute inset-0 rounded-lg border-2 border-primary/40 animate-help-ping" />
            </button>
          </div>
        </div>
      </header>
      )}

      <HelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title={HELP['sales.documents.form'].title}
        sections={HELP['sales.documents.form'].sections}
      />

      <form id="dte-form" onSubmit={handleSubmit(onSubmit)}>
        <div className={embedded ? 'px-1 py-1 flex gap-6' : 'max-w-7xl mx-auto px-4 sm:px-6 py-5 flex gap-6'}>

          {/* ═══ LEFT: FORM SECTIONS ═══ */}
          <main className={`flex-1 min-w-0 space-y-5 ${embedded ? 'pb-2' : 'pb-20'}`}>

            {/* ── Documento ── */}
            <div data-tour="docform-doctype">
            <Section id="sec-documento" icon={FileText} title="Tipo y Datos del Documento" color="bg-primary-50 text-primary">
              {/* Si el tipo elegido no tiene folios, avisarlo ACÁ y no al apretar
                  "Emitir": quedarse sin folios autorizados impide emitir. */}
              <FolioStatusNotice documentType={watchedDocType} className="mb-4" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                <SelectField
                  dense
                  label="Tipo de Documento"
                  required
                  selectProps={register('document_type')}
                >
                  {DOCUMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </SelectField>
                <div>
                  <label htmlFor="documentformpage-issue-date" className={labelCls}>Fecha de Emisión <span className="text-danger-solid">*</span></label>
                  <Controller name="issue_date" control={control} render={({ field }) =>
                    <DatePicker id="documentformpage-issue-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                  } />
                </div>
                {isFieldEnabled('id_doc.fma_pago') && (
                  <SelectField
                    dense
                    label="Forma de Pago"
                    required
                    selectProps={register('payment_method')}
                  >
                    <option value="contado">Contado</option>
                    <option value="credito">Crédito</option>
                  </SelectField>
                )}

                {emiteEnMonedaExtranjera && (
                  <div>
                    <label className={labelCls}>Moneda del comprobante</label>
                    {/* El comprobante se EMITE en esta moneda: el XML la declara
                        y sus importes van en ella. En Chile no se ofrece porque
                        el DTE del SII no tiene donde declararla. */}
                    <select aria-label="Moneda del comprobante" {...register('currency')} className={inputCls}>
                      <option value="">{baseCurrency} (moneda del negocio)</option>
                      {monedasExtranjeras.map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-content-subtle mt-1">
                      Todas las lineas deben estar en la moneda elegida. Los
                      montos de la contabilidad se calculan con el tipo de cambio
                      del dia.
                    </p>
                  </div>
                )}

                {usaTipoOperacion && (
                  <SelectField
                    dense
                    label="Tipo de operación"
                    selectProps={register('operation_type')}
                    hint="Una exportación sale sin IGV, con la afectación 40 y el tributo EXP. Elija el código que corresponda: el 0200 es de bienes y los 0201 al 0208 de servicios."
                  >
                    {CODIGOS_TIPO_OPERACION_PE.map((o: { codigo: string; descripcion: string }) => (
                    <option key={o.codigo} value={o.codigo}>{o.descripcion}</option>
                    ))}
                  </SelectField>
                )}

                {/* Indicador Traslado */}
                {isFieldEnabled('id_doc.ind_traslado') && (
                  <SelectField
                    dense
                    label="Tipo de traslado"
                    required
                    error={errors.ind_traslado?.message}
                    hint="Si la guía acompaña una venta o solo traslada mercadería. Se informa al SII."
                    // Sin opción vacía, el select mostraba «1 — Operación de venta»
                    // como elegido aunque nadie lo hubiera tocado.
                    selectProps={register('ind_traslado', {
                      validate: v => watchedDocType !== 'GD' || !!v || 'Elegir el tipo de traslado.',
                    })}
                  >
                    <option value="">Elegir…</option>
                    <option value="1">1 — Operación de venta</option>
                    <option value="2">2 — Venta por efectuar</option>
                    <option value="3">3 — Consignación</option>
                    <option value="4">4 — Entrega gratuita</option>
                    <option value="5">5 — Traslado interno</option>
                    <option value="6">6 — Otros traslados no venta</option>
                    <option value="7">7 — Devolución de mercaderías</option>
                    <option value="8">8 — Traslado para exportación (no venta)</option>
                    <option value="9">9 — Venta para exportación</option>
                  </SelectField>
                )}
              </div>

              {/* Credit terms */}
              {watchedPaymentMethod === 'credito' && (
                <div className="mt-4 p-3 bg-primary-50/60 border border-primary-200 rounded-lg">
                  <p className="text-xs font-semibold text-primary-hover mb-2">Plazo de crédito</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[30, 60, 90].map(d => (
                      <button key={d} type="button" onClick={() => setValue('payment_term_days', d)}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                          watchedPaymentTermDays === d
                            ? 'bg-primary text-on-primary border-primary'
                            : 'bg-surface text-content-muted border-divider hover:bg-soft'
                        }`}
                      >{d} días</button>
                    ))}
                    <input aria-label="Plazo de crédito en días" type="number" min="1" max="365" {...register('payment_term_days', { valueAsNumber: true })}
                      className="w-20 px-2.5 py-1.5 text-xs border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200/20"
                      placeholder="Días"
                    />
                    <span className="text-xs text-content-muted">días</span>
                  </div>
                  {dueDate && <p className="text-xs text-primary mt-1.5">Vencimiento: {dueDate}</p>}
                </div>
              )}
            </Section>
            </div>

            {/* ── Receptor ── */}
            <div data-tour="docform-customer">
            <Section id="sec-receptor" icon={UserCircle} title="Receptor" color="bg-success-surface text-success-content-soft">
              <div className="mb-4">
                <label htmlFor="documentformpage-customer" className={labelCls}>Buscar Cliente <span className="text-danger-solid">*</span></label>
                <Controller name="customer" control={control} rules={{ required: 'El cliente es obligatorio.' }}
                  render={({ field }) => (
                    <Autocomplete id="documentformpage-customer"
                      items={customerItems}
                      value={field.value}
                      displayValue={selectedCustomer ? `${selectedCustomer.legal_name}${selectedCustomer.tax_id ? ` (${formatRut(selectedCustomer.tax_id)})` : ''}` : ''}
                      onChange={field.onChange}
                      placeholder="Buscar por nombre o RUT..."
                      className={inputCls}
                      required
                    />
                  )}
                />
                {errors.customer && <p className="mt-1 text-xs text-danger-solid">{errors.customer.message}</p>}
              </div>
              {selectedCustomer && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3">
                  <div><label htmlFor="documentformpage-rut" className={labelCls}>RUT</label><input id="documentformpage-rut" readOnly value={formatRut(selectedCustomer.tax_id || '')} className={readonlyCls} /></div>
                  <div><label htmlFor="documentformpage-razon-social" className={labelCls}>Razón Social</label><input id="documentformpage-razon-social" readOnly value={selectedCustomer.legal_name || ''} className={readonlyCls} /></div>
                  <div><label htmlFor="documentformpage-giro" className={labelCls}>Giro</label><input id="documentformpage-giro" readOnly value={selectedCustomer.giro || ''} className={readonlyCls} /></div>
                  <div><label htmlFor="documentformpage-direccion" className={labelCls}>Dirección</label><input id="documentformpage-direccion" readOnly value={selectedCustomer.address_street || ''} className={readonlyCls} /></div>
                  <div><label htmlFor="documentformpage-comuna" className={labelCls}>Comuna</label><input id="documentformpage-comuna" readOnly value={selectedCustomer.address_commune || ''} className={readonlyCls} /></div>
                  <div><label htmlFor="documentformpage-correo-dte" className={labelCls}>Correo DTE</label><input id="documentformpage-correo-dte" readOnly value={(selectedCustomer as any).email_dte || ''} className={readonlyCls} /></div>
                </div>
              )}
            </Section>
            </div>

            {/* ── Transporte (GD o habilitado en config) ── */}
            {isFieldEnabled('transporte') && (
              <Section id="sec-transporte" icon={Truck} title="Transporte" color="bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300"
                badge={<span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300 border border-sky-200 dark:border-sky-800 font-medium">Guía de Despacho</span>}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                  <div><label htmlFor="documentformpage-transporte-patente" className={labelCls}>Patente Vehículo {marcaObligatoria}</label><input id="documentformpage-transporte-patente" {...register('transporte.patente', transporteRequerido('la patente del vehículo'))} className={inputCls} maxLength={8} placeholder="ABCD-12" />{errorDeTransporte('patente')}</div>
                  <div><label htmlFor="documentformpage-transporte-rut-trans" className={labelCls}>RUT Transportista {marcaObligatoria}</label><input id="documentformpage-transporte-rut-trans" {...register('transporte.rut_trans', transporteRequerido('el RUT del transportista'))} className={inputCls} placeholder="76.123.456-7" />{errorDeTransporte('rut_trans')}</div>
                  <div><label htmlFor="documentformpage-transporte-patente-carro" className={labelCls}>Patente Carro / Remolque</label><input id="documentformpage-transporte-patente-carro" {...register('transporte.patente_carro')} className={inputCls} maxLength={8} placeholder="Opcional" /></div>
                  <div><label htmlFor="documentformpage-transporte-chofer-rut" className={labelCls}>RUT Chofer {marcaObligatoria}</label><input id="documentformpage-transporte-chofer-rut" {...register('transporte.chofer_rut', transporteRequerido('el RUT del chofer'))} className={inputCls} placeholder="12.345.678-9" />{errorDeTransporte('chofer_rut')}</div>
                  <div><label htmlFor="documentformpage-transporte-chofer-nombre" className={labelCls}>Nombre Chofer {marcaObligatoria}</label><input id="documentformpage-transporte-chofer-nombre" {...register('transporte.chofer_nombre', transporteRequerido('el nombre del chofer'))} className={inputCls} />{errorDeTransporte('chofer_nombre')}</div>
                  <div><label htmlFor="documentformpage-transporte-dir-dest" className={labelCls}>Dirección Destino {marcaObligatoria}</label><input id="documentformpage-transporte-dir-dest" {...register('transporte.dir_dest', transporteRequerido('la dirección de destino'))} className={inputCls} />{errorDeTransporte('dir_dest')}</div>
                  <div><label htmlFor="documentformpage-transporte-cmna-dest" className={labelCls}>Comuna Destino {marcaObligatoria}</label><input id="documentformpage-transporte-cmna-dest" {...register('transporte.cmna_dest', transporteRequerido('la comuna de destino'))} className={inputCls} />{errorDeTransporte('cmna_dest')}</div>
                  <div>
                    <label htmlFor="documentformpage-transporte-fch-salida" className={labelCls}>Fecha de Salida {marcaObligatoria}</label>
                    <Controller name="transporte.fch_salida" control={control} rules={transporteRequerido('la fecha de salida')} render={({ field }) =>
                      <DatePicker id="documentformpage-transporte-fch-salida" value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} />
                    } />
                    {errorDeTransporte('fch_salida')}
                  </div>
                  <TextField
                    dense
                    label="Hora de Salida"
                    inputProps={{ ...register('transporte.hra_salida'), type: 'time', step: '1' }}
                  />
                  <div>
                    <label htmlFor="documentformpage-transporte-fch-llegada" className={labelCls}>Fecha de Llegada</label>
                    <Controller name="transporte.fch_llegada" control={control} render={({ field }) =>
                      <DatePicker id="documentformpage-transporte-fch-llegada" value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} />
                    } />
                  </div>
                </div>
              </Section>
            )}

            {/* ── Aduana / Exportación (FEX/NDX/NCX o habilitado en config) ── */}
            {isFieldEnabled('aduana') && (
              <Section id="sec-aduana" icon={Globe2} title="Aduana / Exportación" color="bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-300"
                badge={<span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800 font-medium">Exportación</span>}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                  <SelectField
                    dense
                    label="Modalidad de Venta"
                    required
                    selectProps={register('aduana.cod_mod_venta')}
                  >
                    <option value="">Seleccione...</option>
                    <option value="1">1 — A firme</option>
                    <option value="2">2 — Bajo condición</option>
                    <option value="4">4 — En consignación libre</option>
                    <option value="9">9 — Sin valor comercial</option>
                  </SelectField>
                  <SelectField
                    dense
                    label="Cláusula de Venta"
                    required
                    selectProps={register('aduana.cod_clau_venta')}
                  >
                    <option value="">Seleccione...</option>
                    <option value="1">CIF</option>
                    <option value="2">FOB</option>
                    <option value="3">EXW</option>
                    <option value="5">FCA</option>
                  </SelectField>
                  <div><label htmlFor="documentformpage-aduana-tot-clau-venta" className={labelCls}>Total Cláusula Venta <span className="text-danger-solid">*</span></label><input id="documentformpage-aduana-tot-clau-venta" type="number" step="0.01" {...register('aduana.tot_clau_venta')} className={inputCls} /></div>
                  <SelectField
                    dense
                    label="Vía de Transporte"
                    required
                    selectProps={register('aduana.cod_via_transp')}
                  >
                    <option value="">Seleccione...</option>
                    <option value="1">1 — Marítima</option>
                    <option value="4">4 — Aérea</option>
                    <option value="5">5 — Terrestre</option>
                  </SelectField>
                  <div><label htmlFor="documentformpage-aduana-cod-pto-embarque" className={labelCls}>Puerto Embarque <span className="text-danger-solid">*</span></label><input id="documentformpage-aduana-cod-pto-embarque" {...register('aduana.cod_pto_embarque')} className={inputCls} /></div>
                  <div><label htmlFor="documentformpage-aduana-cod-pto-desemb" className={labelCls}>Puerto Desembarque <span className="text-danger-solid">*</span></label><input id="documentformpage-aduana-cod-pto-desemb" {...register('aduana.cod_pto_desemb')} className={inputCls} /></div>
                  <div><label htmlFor="documentformpage-aduana-peso-bruto" className={labelCls}>Peso Bruto (kg) <span className="text-danger-solid">*</span></label><input id="documentformpage-aduana-peso-bruto" type="number" step="0.01" {...register('aduana.peso_bruto')} className={inputCls} /></div>
                  <div><label htmlFor="documentformpage-aduana-peso-neto" className={labelCls}>Peso Neto (kg) <span className="text-danger-solid">*</span></label><input id="documentformpage-aduana-peso-neto" type="number" step="0.01" {...register('aduana.peso_neto')} className={inputCls} /></div>
                  <div><label htmlFor="documentformpage-aduana-cod-pais-dest" className={labelCls}>País Destino <span className="text-danger-solid">*</span></label><input id="documentformpage-aduana-cod-pais-dest" {...register('aduana.cod_pais_dest')} className={inputCls} placeholder="US, AR, BR..." /></div>
                </div>
              </Section>
            )}

            {/* ── Líneas de Detalle ── */}
            <div data-tour="docform-lines">
            <Section id="sec-detalle" icon={List} title="Líneas de Detalle" color="bg-info-surface text-info-content-soft"
              headerRight={
                <button type="button" onClick={() => appendLine({ ...EMPTY_LINE })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors border border-primary-200"
                >
                  <Plus size={14} /> Agregar línea
                </button>
              }
            >
              <div className="-mx-5">
                {/*
                  Cada fila llega con su posición y con lo que se deriva de ella
                  —la moneda, el subtotal en las dos monedas, sus centros de
                  costo—, porque son varias las columnas que lo usan: calcularlo
                  por columna lo repetiría cuatro veces por fila.

                  ⚠️ La fuente de la LONGITUD es `lineFields` y no
                  `watchedLines`: el primero crece en el mismo render en que se
                  agrega la línea y el segundo recién en el siguiente, así que
                  ahí el índice nuevo llega `undefined`.

                  ⚠️ Y NO va en un `useMemo`: sus entradas se recrean en cada
                  render, así que memoizarlo recalcularía igual y solo agregaría
                  la ilusión de que no.
                */}
                <DataTable<(typeof filasDeLinea)[number]>
                  columns={[
                    { key: 'n', header: '#', width: 32,
                      render: f => <span className="text-content-subtle">{f.idx + 1}</span> },
                    { key: 'sku', header: 'SKU', width: 80,
                      render: f => (
                        <Controller name={`lines.${f.idx}.sku`} control={control} render={({ field: c }) => (
                          <ProductAutocomplete field="sku" value={c.value}
                            onChange={c.onChange}
                            onSelectProduct={(prod) => { recordarProducto(prod); fillProduct(f.idx, prod) }}
                            placeholder="SKU" className={cellInputCls}
                            aria-label={`SKU de la línea ${f.idx + 1}`}
                          />
                        )} />
                      ) },
                    { key: 'name', width: 180,
                      header: <>Nombre <span className="text-danger-solid">*</span></>,
                      render: f => (
                        <Controller name={`lines.${f.idx}.name`} control={control} render={({ field: c }) => (
                          <ProductAutocomplete field="name" value={c.value}
                            onChange={c.onChange}
                            onSelectProduct={(prod) => { recordarProducto(prod); fillProduct(f.idx, prod) }}
                            placeholder="Nombre del ítem" className={cellInputCls}
                            aria-label={`Nombre de la línea ${f.idx + 1}`}
                          />
                        )} />
                      ) },
                    { key: 'quantity', numeric: true, width: 64,
                      header: <>Cant. <span className="text-danger-solid">*</span></>,
                      render: f => (
                        <>
                          <Controller name={`lines.${f.idx}.quantity`} control={control}
                            render={({ field }) => (
                              <NumericInput {...field} locale={locale} decimals={2}
                                aria-label={`Cantidad de la línea ${f.idx + 1}`}
                                className={`${cellInputCls} text-right`} />
                            )} />
                          {(() => {
                            const prod = findLineProduct(f.linea)
                            if (!prod?.track_stock) return null
                            const available = stockMap[prod.id] ?? 0
                            const qty = parseFloat(f.linea?.quantity) || 0
                            if (qty <= available) return null
                            return (
                              <div className="flex items-center gap-1 mt-0.5 text-warning-content-soft" title={`Stock disponible: ${available}`}>
                                <AlertTriangle size={11} />
                                <span className="text-[10px] font-medium">
                                  {available <= 0 ? 'Sin stock' : `Stock: ${available}`}
                                </span>
                              </div>
                            )
                          })()}
                        </>
                      ) },
                    { key: 'unit', header: 'Unid.', width: 80,
                      render: f => (
                        <select {...register(`lines.${f.idx}.unit`)}
                          aria-label={`Unidad de la línea ${f.idx + 1}`}
                          className={`${cellInputCls} min-w-[52px]`}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      ) },
                    { key: 'unit_price', numeric: true, width: 128,
                      header: <>Precio <span className="text-danger-solid">*</span></>,
                      render: f => (
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const lineProd = findLineProduct(f.linea)
                            const priceOpts = productPriceOptions(lineProd)
                            // Selector de moneda solo si el producto tiene >1 precio.
                            if (lineProd && priceOpts.length > 1) {
                              return (
                                <select
                                  value={f.cur}
                                  onChange={(e) => changeLineCurrency(f.idx, lineProd, e.target.value)}
                                  title="Moneda del precio"
                                  aria-label={`Moneda del precio de la línea ${f.idx + 1}`}
                                  className="text-[9px] font-bold bg-primary-100 text-primary-hover rounded px-1 py-0.5 border-0 cursor-pointer focus:ring-1 focus:ring-primary"
                                >
                                  {priceOpts.map(o => <option key={o.currency} value={o.currency}>{o.currency}</option>)}
                                </select>
                              )
                            }
                            return f.cur !== baseCurrency ? (
                              <LineTooltip
                                content={
                                  <>
                                    <p className="text-[11px] font-semibold">{fmtCLP(f.price)} por unidad</p>
                                    {f.rate && <p className="text-[10px] text-content-muted mt-0.5">1 {f.cur} = {fmtTipoDeCambio(f.rate, f.cur)}</p>}
                                  </>
                                }
                              >
                                <span className="text-[9px] font-bold bg-primary-100 text-primary-hover rounded px-1 py-0.5 whitespace-nowrap cursor-help select-none">
                                  {f.cur}
                                </span>
                              </LineTooltip>
                            ) : null
                          })()}
                          <div className="flex-1">
                            <Controller name={`lines.${f.idx}.unit_price`} control={control}
                              render={({ field }) => (
                                <NumericInput {...field} locale={locale} decimals={3}
                                  aria-label={`Precio unitario de la línea ${f.idx + 1}`}
                                  className={`${cellInputCls} text-right`} />
                              )} />
                          </div>
                        </div>
                      ) },
                    { key: 'discount_pct', header: 'Dto.%', numeric: true, width: 56,
                      render: f => (
                        <Controller name={`lines.${f.idx}.discount_pct`} control={control}
                          render={({ field }) => (
                            <NumericInput {...field} locale={locale} decimals={2}
                              aria-label={`Descuento porcentual de la línea ${f.idx + 1}`}
                              className={`${cellInputCls} text-right`} />
                          )} />
                      ) },
                    { key: 'subtotal', header: 'Subtotal', numeric: true, width: 96,
                      render: f => (
                        <span className="font-medium text-content">
                          {f.cur !== baseCurrency && f.rate ? (
                            <LineTooltip
                              content={
                                <>
                                  <p className="text-[11px] font-semibold">
                                    {f.lineSubForeign.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {f.cur}
                                  </p>
                                  <p className="text-[10px] text-content-muted mt-0.5">1 {f.cur} = {fmtTipoDeCambio(f.rate, f.cur)}</p>
                                </>
                              }
                            >
                              <span className="cursor-help">{fmtCLP(f.lineSub)}</span>
                            </LineTooltip>
                          ) : (
                            fmtCLP(f.lineSub)
                          )}
                        </span>
                      ) },
                    { key: 'acciones', header: '', width: 48,
                      render: f => (
                        <div className="flex flex-col items-center gap-0.5">
                          {/* ⚠️ El campo oculto vive DENTRO de esta celda y no en
                              una propia: un `<td className="hidden">` deja al
                              `<tr>` con una celda más que encabezados tiene la
                              tabla, y era por eso que el detalle de abajo se
                              abría con un `colSpan={10}` sobre nueve columnas. */}
                          <input type="hidden" {...register(`lines.${f.idx}.is_tax_exempt`)} />
                          <button
                            type="button"
                            onClick={() => toggleLine(f.idx)}
                            title="Descripción adicional / Centros de Costo"
                            aria-expanded={expandedLines.has(f.idx)}
                            aria-label={`Descripción y centros de costo de la línea ${f.idx + 1}`}
                            className={`relative p-1 rounded transition-colors ${
                              expandedLines.has(f.idx) || f.hasExtras
                                ? 'text-info-content-soft hover:bg-info-surface'
                                : 'text-content-subtle hover:text-info-solid hover:bg-info-surface'
                            }`}
                          >
                            <Building2 size={13} />
                            {f.lineCC.length > 0 && (
                              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-info-solid text-white text-[7px] flex items-center justify-center font-bold leading-none">
                                {f.lineCC.length}
                              </span>
                            )}
                          </button>
                          {lineFields.length > 1 && (
                            <button type="button" onClick={() => handleRemoveLine(f.idx)}
                              aria-label={`Eliminar la línea ${f.idx + 1}`}
                              className="p-1 rounded hover:bg-danger-surface text-content-subtle hover:text-danger-solid transition-colors">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      ) },
                  ]}
                  rows={filasDeLinea}
                  rowKey={f => f.id}
                  minWidth={800}
                  surface={false}
                  rowExpanded={f => expandedLines.has(f.idx)}
                  expandedRow={f => (
                    <div className="px-5 py-3 border-b border-info-border bg-violet-50/40 dark:bg-violet-900/20">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] font-semibold text-info-solid uppercase tracking-wider mb-1.5">
                            Descripción adicional de línea
                          </p>
                          <input
                            {...register(`lines.${f.idx}.description`)}
                            aria-label={`Descripción adicional de la línea ${f.idx + 1}`}
                            className="w-full px-2 py-1.5 text-xs border border-divider rounded bg-surface focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 placeholder:text-content-subtle transition-all"
                            placeholder="Detalle adicional que aparece en el DTE (DscItem)…"
                          />
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-info-solid uppercase tracking-wider mb-1.5">
                            Centros de Costo
                          </p>
                          <CostCenterAllocator
                            allocations={f.lineCC as CostCenterAllocation[]}
                            onChange={(allocs) => setValue(`lines.${f.idx}.cost_center_allocations` as any, allocs)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  caption="Líneas del documento de venta"
                />
              </div>
            </Section>
            </div>

            {/* ── Descuentos / Recargos Globales ── */}
            {isFieldEnabled('dsc_rcg_global') && <Section id="sec-descuentos" icon={Tag} title="Descuentos / Recargos Globales" color="bg-warning-surface text-warning-content-soft"
              badge={<span className="text-[10px] px-1.5 py-0.5 rounded bg-success-surface text-success-content-soft border border-success-border font-medium">Opcional</span>}
              headerRight={
                <button type="button" onClick={() => appendDscRcg({ ...EMPTY_DSC_RCG })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-warning-content-soft bg-warning-surface rounded-lg hover:bg-warning-surface-strong transition-colors border border-warning-border"
                >
                  <Plus size={14} /> Agregar
                </button>
              }
            >
              {dscRcgFields.length === 0 ? (
                <p className="text-xs text-content-subtle italic">Sin descuentos ni recargos globales. Los descuentos por línea se configuran en la columna "Dto.%" del detalle.</p>
              ) : (
                <div className="-mx-5">
                  <DataTable<FilaDeFormulario>
                    columns={[
                      { key: 'n', header: '#', width: 32,
                        render: f => <span className="text-content-subtle">{f.idx + 1}</span> },
                      { key: 'tpo_mov', header: 'Tipo', width: 112,
                        render: f => (
                          <select {...register(`dsc_rcg_global.${f.idx}.tpo_mov`)}
                            aria-label={`Tipo del movimiento ${f.idx + 1}`}
                            className={cellInputCls}>
                            <option value="D">Descuento</option>
                            <option value="R">Recargo</option>
                          </select>
                        ) },
                      { key: 'glosa_dr', header: 'Glosa',
                        render: f => (
                          <input {...register(`dsc_rcg_global.${f.idx}.glosa_dr`)}
                            className={cellInputCls} placeholder="Descripción"
                            aria-label={`Glosa del movimiento ${f.idx + 1}`} />
                        ) },
                      { key: 'tpo_valor', header: 'Tipo Valor', width: 80,
                        render: f => (
                          <select {...register(`dsc_rcg_global.${f.idx}.tpo_valor`)}
                            aria-label={`Tipo de valor del movimiento ${f.idx + 1}`}
                            className={cellInputCls}>
                            <option value="%">%</option>
                            <option value="$">$</option>
                          </select>
                        ) },
                      { key: 'valor_dr', header: 'Valor', numeric: true, width: 112,
                        render: f => (
                          <Controller name={`dsc_rcg_global.${f.idx}.valor_dr`} control={control}
                            render={({ field }) => (
                              <NumericInput {...field} locale={locale} decimals={2}
                                aria-label={`Valor del movimiento ${f.idx + 1}`}
                                className={`${cellInputCls} text-right`} placeholder="0" />
                            )} />
                        ) },
                      { key: 'quitar', header: '', width: 40,
                        render: f => (
                          <div className="text-center">
                            <button type="button" onClick={() => removeDscRcg(f.idx)}
                              aria-label={`Eliminar el movimiento ${f.idx + 1}`}
                              className="p-1 rounded hover:bg-danger-surface text-content-subtle hover:text-danger-solid transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ) },
                    ]}
                    rows={dscRcgFields.map((field, idx) => ({ id: field.id, idx }))}
                    rowKey={f => f.id}
                    minWidth={600}
                    surface={false}
                    caption="Descuentos y recargos globales del documento"
                  />
                </div>
              )}
            </Section>}

            {/* ── Referencias (siempre visible, obligatorio para NC/ND) ── */}
            <Section id="sec-referencias" icon={Link2} title="Referencias" color="bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300"
              badge={REFERENCE_TYPES.has(watchedDocType)
                ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-danger-surface text-danger-content-soft border border-danger-border font-medium">Obligatorio</span>
                : <span className="text-[10px] px-1.5 py-0.5 rounded bg-success-surface text-success-content-soft border border-success-border font-medium">Opcional</span>
              }
              headerRight={
                <button type="button" onClick={() => appendRef({ ...EMPTY_REF })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors border border-orange-200 dark:border-orange-800"
                >
                  <Plus size={14} /> Agregar referencia
                </button>
              }
            >
              {refFields.length === 0 ? (
                <p className="text-xs text-content-subtle italic">
                  {REFERENCE_TYPES.has(watchedDocType)
                    ? 'Debe agregar al menos una referencia al documento original.'
                    : 'Sin referencias. Puede referenciar órdenes de compra, guías de despacho u otros documentos.'}
                </p>
              ) : (
                <div className="-mx-5">
                  {/*
                    Las columnas se arman con un spread condicional y no con un
                    `<th>`/`<td>` por `isFieldEnabled`: escritas a mano, el
                    encabezado y la celda son DOS condiciones que hay que
                    mantener iguales, y basta que una cambie para que la tabla
                    entera se corra una columna. Acá la condición se escribe una
                    sola vez.
                  */}
                  <DataTable<FilaDeFormulario>
                    columns={[
                      { key: 'n', header: '#', width: 40,
                        render: (f: FilaDeFormulario) => <span className="text-content-subtle">{f.idx + 1}</span> },
                      ...(isFieldEnabled('referencia.tpo_doc_ref') ? [{
                        key: 'tipo_doc', header: 'Tipo Doc.', width: 208,
                        render: (f: FilaDeFormulario) => (
                          <select {...register(`referencias.${f.idx}.tipo_doc`)}
                            aria-label={`Tipo de documento de la referencia ${f.idx + 1}`}
                            className={cellInputCls}>
                            <option value="">— Seleccione —</option>
                            {REF_DOC_TYPES.map(g => (
                              <optgroup key={g.group} label={g.group}>
                                {g.options.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        ),
                      }] : []),
                      ...(isFieldEnabled('referencia.folio_ref') ? [{
                        key: 'folio_ref', header: 'Folio', width: 96,
                        render: (f: FilaDeFormulario) => (
                          <input {...register(`referencias.${f.idx}.folio_ref`)}
                            aria-label={`Folio de la referencia ${f.idx + 1}`}
                            className={cellInputCls} />
                        ),
                      }] : []),
                      ...(isFieldEnabled('referencia.fch_ref') ? [{
                        key: 'fecha_ref', header: 'Fecha', width: 144,
                        render: (f: FilaDeFormulario) => (
                          <Controller name={`referencias.${f.idx}.fecha_ref`} control={control} render={({ field: c }) =>
                            <DatePicker aria-label="Fecha" value={c.value} onChange={c.onChange} onBlur={c.onBlur} />
                          } />
                        ),
                      }] : []),
                      ...(isFieldEnabled('referencia.ind_global') ? [{
                        key: 'ind_global', header: 'Global', width: 56,
                        render: (f: FilaDeFormulario) => (
                          <div className="text-center">
                            <input type="checkbox" {...register(`referencias.${f.idx}.ind_global` as any)}
                              aria-label={`Referencia ${f.idx + 1} global`}
                              className="rounded border-divider-strong text-primary w-3.5 h-3.5" />
                          </div>
                        ),
                      }] : []),
                      ...(isFieldEnabled('referencia.rut_otr') ? [{
                        key: 'rut_otr', header: 'RUT Otro', width: 112,
                        render: (f: FilaDeFormulario) => (
                          <input {...register(`referencias.${f.idx}.rut_otr` as any)}
                            className={cellInputCls} placeholder="RUT"
                            aria-label={`RUT del otro contribuyente, referencia ${f.idx + 1}`} />
                        ),
                      }] : []),
                      ...(isFieldEnabled('referencia.cod_ref') ? [{
                        key: 'cod_ref', header: 'Código Ref.', width: 160,
                        render: (f: FilaDeFormulario) => (
                          <select {...register(`referencias.${f.idx}.cod_ref`)}
                            aria-label={`Código de la referencia ${f.idx + 1}`}
                            className={cellInputCls}>
                            <option value="">—</option>
                            <option value="1">1 — Anula</option>
                            <option value="2">2 — Corrige texto</option>
                            <option value="3">3 — Corrige montos</option>
                          </select>
                        ),
                      }] : []),
                      ...(isFieldEnabled('referencia.razon_ref') ? [{
                        key: 'razon_ref', header: 'Razón',
                        render: (f: FilaDeFormulario) => (
                          <input {...register(`referencias.${f.idx}.razon_ref`)}
                            aria-label={`Razón de la referencia ${f.idx + 1}`}
                            className={cellInputCls} />
                        ),
                      }] : []),
                      { key: 'quitar', header: '', width: 40,
                        render: (f: FilaDeFormulario) => (
                          <div className="text-center">
                            <button type="button" onClick={() => removeRef(f.idx)}
                              aria-label={`Eliminar la referencia ${f.idx + 1}`}
                              className="p-1 rounded hover:bg-danger-surface text-content-subtle hover:text-danger-solid transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ) },
                    ]}
                    rows={refFields.map((field, idx) => ({ id: field.id, idx }))}
                    rowKey={f => f.id}
                    surface={false}
                    caption="Documentos referenciados por este documento"
                  />
                </div>
              )}
            </Section>

            {/* ── Comisiones (LF o habilitado en config) ── */}
            {isFieldEnabled('comisiones') && (
              <Section id="sec-comisiones" icon={Banknote} title="Comisiones y Otros Cargos" color="bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300"
                badge={<span className="text-[10px] px-1.5 py-0.5 rounded bg-danger-surface text-danger-content-soft border border-danger-border font-medium">Obligatorio</span>}
              >
                <p className="text-xs text-content-subtle italic">Sección de comisiones para Liquidación-Factura (tipo 43).</p>
              </Section>
            )}

            {/* ── Campos Personalizados ── */}
            {globalCampoFields.length > 0 && (
              <Section id="sec-campos" icon={ClipboardList} title="Campos Personalizados" color="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300"
                badge={<span className="text-[10px] text-content-subtle">{globalCampoFields.length} campo{globalCampoFields.length !== 1 ? 's' : ''}</span>}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                  {globalCampoFields.map((field, idx) => {
                    const isRequired = field.is_required
                    const tipo = field.tipo
                    return (
                      <div key={field.id} className={tipo === 'boolean' ? 'flex items-center gap-2 pt-4' : ''}>
                        {tipo === 'boolean' ? (
                          <>
                            <Controller name={`global_campos.${idx}.valor`} control={control}
                              render={({ field: f }) => (
                                <input type="checkbox" aria-label={field.nombre}
                                  checked={f.value === 'true'}
                                  onChange={e => f.onChange(e.target.checked ? 'true' : 'false')}
                                  className="h-4 w-4 rounded border-divider-strong text-primary focus:ring-primary"
                                />
                              )}
                            />
                            <label className="text-xs text-content">
                              {field.nombre}
                              {isRequired && <span className="text-danger-solid ml-0.5">*</span>}
                            </label>
                          </>
                        ) : (
                          <>
                            <label className={labelCls}>
                              {field.nombre}
                              {isRequired && <span className="text-danger-solid ml-0.5">*</span>}
                            </label>
                            {tipo === 'select' ? (
                              <select aria-label={field.nombre} {...register(`global_campos.${idx}.valor`, {
                                required: isRequired ? `${field.nombre} es obligatorio.` : false,
                              })} className={inputCls}>
                                <option value="">Seleccione…</option>
                                {(field.opciones || []).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : tipo === 'fecha' ? (
                              <Controller name={`global_campos.${idx}.valor`} control={control}
                                rules={{ required: isRequired ? `${field.nombre} es obligatorio.` : false }}
                                render={({ field: f }) => (
                                  <DatePicker aria-label={field.nombre} value={f.value} onChange={f.onChange} onBlur={f.onBlur} />
                                )}
                              />
                            ) : (
                              <input aria-label={field.nombre} type={tipo === 'numero' ? 'number' : 'text'}
                                {...register(`global_campos.${idx}.valor`, {
                                  required: isRequired ? `${field.nombre} es obligatorio.` : false,
                                })}
                                className={inputCls}
                              />
                            )}
                            {errors.global_campos?.[idx]?.valor && (
                              <p className="mt-0.5 text-xs text-danger-solid">{errors.global_campos[idx]?.valor?.message}</p>
                            )}
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              </Section>
            )}

            {/* ── Adjuntos ── */}
            <Section id="sec-adjuntos" icon={Paperclip} title="Archivos Adjuntos" color="bg-warning-surface text-warning-content-soft">
              {!isEditing ? (
                <PendingAttachmentsPicker files={pendingFiles} onChange={setPendingFiles} />
              ) : (
                <>
                <p className="text-xs text-content-muted mb-3">
                  Máx. 20 MB total. Tipos permitidos: PDF, JPEG, PNG, WEBP.
                </p>
                <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-dashed border-divider-strong hover:border-primary hover:bg-primary-50 transition-colors text-xs text-content-muted hover:text-primary">
                  <Upload size={14} />
                  {uploadAttachment.isPending ? (
                    <span className="flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Subiendo...</span>
                  ) : (
                    'Subir archivo'
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileUpload}
                    disabled={uploadAttachment.isPending}
                    className="hidden"
                  />
                </label>
                {uploadError && (
                  <p className="mt-2 text-xs text-danger-solid">{uploadError}</p>
                )}
                {attachmentsData && attachmentsData.count > 0 ? (
                  <div className="mt-4 space-y-2">
                    {attachmentsData.attachments.map(att => (
                      <div key={att.id} className="flex items-center gap-3 p-2.5 bg-glass-3 rounded-lg border border-divider group">
                        <Paperclip size={14} className="text-content-subtle flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-content truncate">{att.file_name}</p>
                          <p className="text-[10px] text-content-subtle">
                            {(att.file_size / 1024).toFixed(1)} KB
                            {att.uploaded_by_name && ` · ${att.uploaded_by_name}`}
                          </p>
                        </div>
                        <a
                          href={att.download_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 text-content-subtle hover:text-primary transition-colors"
                          title="Ver"
                        >
                          <Eye size={13} />
                        </a>
                        <button
                          type="button"
                          onClick={() => deleteAttachment.mutate(att.id)}
                          disabled={deleteAttachment.isPending}
                          className="p-1.5 text-content-subtle hover:text-danger-solid transition-colors opacity-0 group-hover:opacity-100"
                          title="Eliminar"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  attachmentsData && (
                    <p className="mt-3 text-xs text-content-subtle italic">Sin archivos adjuntos.</p>
                  )
                )}
                </>
              )}
            </Section>

            {/* ── Notas ── */}
            <Section id="sec-notas" icon={StickyNote} title="Notas Internas" color="bg-surface-muted text-content-muted">
              <textarea aria-label="Notas internas" {...register('notes')} rows={2} placeholder="Observaciones internas (no aparecen en el DTE)..."
                className={inputCls + ' resize-y'}
              />
            </Section>

          </main>

          {/* ═══ RIGHT: STICKY SIDEBAR ═══ (oculto en 360: resumen/acciones van al header) */}
          <aside data-tour="docform-sidebar" className={`w-[280px] flex-shrink-0 ${embedded ? 'hidden' : 'hidden lg:block'}`}>
            <div className="sticky top-16 space-y-4">

              {/* Totals */}
              <div className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-slate-800">
                  <div className="text-[10px] uppercase tracking-wider text-white font-semibold">Resumen</div>
                  <div className="text-xs text-white/70 mt-0.5">
                    {DOCUMENT_TYPES.find(t => t.value === watchedDocType)?.label || watchedDocType}
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-content-muted">Neto</span>
                    <span className="text-sm font-semibold text-content tabular-nums">{fmtCLP(montoNeto)}</span>
                  </div>
                  {montoExento > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-content-muted">Exento</span>
                      <span className="text-sm font-semibold text-content tabular-nums">{fmtCLP(montoExento)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-content-muted">{tax.name} ({taxRate}%)</span>
                    <span className="text-sm font-semibold text-content tabular-nums">{fmtCLP(montoIVA)}</span>
                  </div>
                  <div className="border-t border-divider pt-3 flex items-center justify-between">
                    <span className="text-xs font-semibold text-content">TOTAL</span>
                    <span className="text-lg font-bold text-primary tabular-nums">{fmtCLP(total)}</span>
                  </div>
                  <div className="text-[10px] text-content-subtle text-right">
                    {lineCount} línea{lineCount !== 1 ? 's' : ''}
                  </div>
                  {foreignCurrency && currencyRates && currencyRates[foreignCurrency] && (
                    <div className="border-t border-divider pt-2 rounded-lg bg-primary-50/60 p-2 -mx-0.5">
                      <p className="text-[10px] font-semibold text-primary uppercase tracking-wide mb-1">Tipo de cambio</p>
                      <p className="text-sm font-semibold text-content">
                        1 {foreignCurrency} = {new Intl.NumberFormat(locale, { style: 'currency', currency: baseCurrency, minimumFractionDigits: 2, maximumFractionDigits: foreignCurrency === 'UF' ? 4 : 2 }).format(parseFloat(currencyRates[foreignCurrency].rate))}
                      </p>
                      <p className="text-[10px] text-content-muted mt-0.5">
                        Actualizado: {currencyRates[foreignCurrency].rate_date}
                      </p>
                      {parseFloat(currencyRates[foreignCurrency].rate) > 0 && (
                        <div className="flex items-center justify-between border-t border-primary-200/60 mt-1.5 pt-1.5">
                          <span className="text-[10px] font-semibold text-primary uppercase tracking-wide">Total {foreignCurrency}</span>
                          <span className="text-sm font-bold text-content tabular-nums">
                            {(total / parseFloat(currencyRates[foreignCurrency].rate)).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Situación financiera del cliente (Form 360, Fase 4b) */}
              <CustomerFinancialCard customerId={watchedCustomerId} />

              {/* Section Nav */}
              <div className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-sm p-4">
                <div className="text-[10px] uppercase tracking-wider text-content-subtle font-semibold mb-3">Secciones</div>
                <nav className="space-y-0.5">
                  {visibleSections.map(s => (
                    <button key={s.id} type="button"
                      onClick={() => {
                        const el = document.getElementById(s.id)
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }}
                      className={`block w-full text-left text-xs py-1.5 pl-3 rounded-md transition-all relative ${
                        activeSection === s.id
                          ? 'text-primary font-semibold bg-primary-50/50'
                          : 'text-content-muted hover:text-primary hover:bg-soft'
                      }`}
                    >
                      {activeSection === s.id && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 bg-primary rounded-r" />
                      )}
                      {s.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button type="submit" disabled={isPending}
                  className="w-full py-2.5 px-4 text-sm font-semibold text-on-primary bg-primary rounded-xl hover:bg-primary-hover active:bg-primary-hover transition-all shadow-sm shadow-primary-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {isEditing ? 'Guardar Cambios' : 'Guardar Borrador'}
                </button>
                <button type="button" onClick={() => navigate('/sales/documents')}
                  className="w-full py-2 px-4 text-xs font-medium text-content-muted bg-surface border border-divider rounded-xl hover:bg-soft transition-all flex items-center justify-center gap-2"
                >
                  Cancelar
                </button>
              </div>

            </div>
          </aside>

        </div>
      </form>

      {/* Mobile bottom bar — oculto en 360 (acciones van al header del workspace) */}
      <div className={`lg:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-divider p-3 flex gap-2 shadow-[0_-2px_10px_rgba(0,0,0,0.06)] z-40 ${embedded ? 'hidden' : ''}`}>
        <div className="flex-1 text-center">
          <div className="text-[10px] text-content-subtle">Total</div>
          <div className="text-sm font-bold text-primary tabular-nums">{fmtCLP(total)}</div>
        </div>
        <button type="button" onClick={() => navigate('/sales/documents')} className="px-4 py-2 text-xs font-medium text-content-muted bg-surface-muted rounded-lg">Cancelar</button>
        <button type="submit" form="dte-form" disabled={isPending} className="px-6 py-2 text-xs font-semibold text-on-primary bg-primary rounded-lg disabled:opacity-50">
          {isPending ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  )
}

// ─── LineTooltip — la ayuda de las celdas de moneda extranjera ──────────────

function LineTooltip({ content, children }: {
  content: React.ReactNode
  children: React.ReactNode
}) {
  // El tooltip del design system (2026-09-30). Reemplaza a uno escrito a mano
  // que no respondía al teclado ni se asociaba por aria-describedby. Va
  // por portal, así que no lo recorta el overflow de la tabla, y se acota solo
  // a la ventana: el `align` que tenía antes ya no hace falta.
  return (
    <Tooltip content={content} side="top" delay={150}>
      {/* tabIndex: sin él el teclado nunca llega al disparador y la ayuda
          queda solo para el mouse (WCAG 1.4.13: al pasar el mouse O al enfocar). */}
      <span className="inline-flex rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring" tabIndex={0}>{children}</span>
    </Tooltip>
  )
}

// ─── Section wrapper ─────────────────────────────────────────────────────────

function Section({ id, icon: Icon, title, color, badge, headerRight, children }: {
  id: string
  icon: typeof FileText
  title: string
  color: string
  badge?: React.ReactNode
  headerRight?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section id={id} className="bg-surface rounded-xl border border-divider shadow-sm scroll-mt-16">
      <div className="px-5 py-3 border-b border-divider flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-md flex items-center justify-center ${color}`}>
            <Icon size={14} />
          </div>
          <h2 className="text-sm font-semibold text-content">{title}</h2>
          {badge}
        </div>
        {headerRight}
      </div>
      <div className="p-5">{children}</div>
    </section>
  )
}
